import { hasRichTextContent } from '../../../helpers/textHelpers';
import { Optional, TextEditorContent } from '../../../types';
import { TextEditor } from '../../TextEditor';

/**
 * Checks if the text children are empty: the inverse of `hasRichTextContent`,
 * kept under its own name for the templates that read it as "empty".
 * @param data - Array of TextEditorContent or undefined.
 * @returns True if all text children are empty, otherwise false.
 */
export const isTextChildrenEmpty = (data: TextEditorContent[] | undefined) => {
  return !hasRichTextContent(data);
};

/**
 * Replaces `{token}` placeholders inside a plain string.
 * @param value - The source string.
 * @param tokens - Map of token name (without braces) to its replacement value.
 * @returns The string with every `{token}` substituted.
 */
export const replaceTokens = (
  value: string,
  tokens: Record<string, string>,
): string => {
  return Object.entries(tokens).reduce((acc, [token, replacement]) => {
    return acc.replace(new RegExp(`\\{${token}\\}`, 'g'), replacement);
  }, value);
};

/**
 * Recursively replaces `{token}` placeholders inside the leaf `.text` nodes of
 * Slate rich-text content. Content without matching tokens is returned
 * unchanged. Mirrors the recursion shape of `isTextChildrenEmpty`.
 * @param content - Array of TextEditorContent (Slate nodes) or undefined.
 * @param tokens - Map of token name (without braces) to its replacement value.
 * @returns New content with tokens substituted, or the input when undefined.
 */
export const replaceTokensInContent = (
  content: TextEditorContent[] | undefined,
  tokens: Record<string, string>,
): TextEditorContent[] | undefined => {
  if (!content) {
    return content;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapNode = (node: any): any => {
    if (typeof node.text === 'string') {
      return { ...node, text: replaceTokens(node.text, tokens) };
    }
    if (node.children) {
      return { ...node, children: node.children.map(mapNode) };
    }
    return node;
  };

  return content.map(mapNode);
};

/**
 * Renders a paragraph or rich text editor based on the content.
 * @param content - String or array of TextEditorContent.
 * @returns JSX element or null.
 */
export const renderParagraphOrRichText = (
  content: Optional<string | TextEditorContent[]>,
  fontFamily?: string,
) => {
  if (typeof content === 'string') {
    return <p>{content}</p>;
  }

  if (content && content.length > 0) {
    // This function validate if children elements from TextEditor content are empty
    // So i can show "Are you sure you want to cancel?" text even if all text are empty
    const isEmpty = isTextChildrenEmpty(content);

    // Always early return
    if (isEmpty) return null;

    return (
      <TextEditor
        viewOnly={true}
        value={content}
        valueFontParagraph={fontFamily}
      />
    );
  }

  return null;
};

// TODO: Temporary solution alert
// This function is used to detect whether the image is an SVG or not, this to address a requirement in [UES-6701], by detecting the SVG the layout will be adjusted to fit the image, keeping the compatibility with the predefined images

/**
 * Checks if the given URL points to an SVG image.
 * @param url - The URL of the image.
 * @returns True if the URL ends with '.svg', otherwise false.
 */
export const isSvg = (url: string): boolean => {
  return url.endsWith('.svg');
};

export const findLocalisation = (localisations, currentLocale) => {
  if (!localisations) {
    return null;
  }

  const [localeInitial] = currentLocale.split('-');

  const exactLocalisationMatch = localisations?.[currentLocale];
  const baseLanguageKeyMatch = localisations?.[localeInitial];

  const localisationTagBasedOnLanguage = Object.keys(localisations).find(key =>
    key.startsWith(`${localeInitial}`),
  );

  const isDefaultLanguageMatch = localisations[Object.keys(localisations)[0]];

  const selectedLocalisation =
    exactLocalisationMatch || //
    baseLanguageKeyMatch ||
    localisations?.[localisationTagBasedOnLanguage] ||
    isDefaultLanguageMatch;

  if (!selectedLocalisation) {
    console.warn(
      `No localisation found for the current locale: ${currentLocale}. Using default localisation.`,
    );
  }

  return selectedLocalisation;
};
